const MAX_EDGE = 1600
const QUALITY = 0.75

export interface CapturedPhoto {
  blob: Blob
  previewUrl: string
  takenAt: string
  originalBytes: number
}

/**
 * Shrinks a camera photo before it ever touches the network.
 *
 * A modern phone produces 3–6 MB per shot. Over a weak mobile connection in a
 * stairwell that is the difference between an upload that finishes and one that
 * does not, and across a year it is the difference between gigabytes and a few
 * hundred megabytes of storage per customer.
 */
export async function compressImage(file: File | Blob): Promise<CapturedPhoto> {
  const originalBytes = file.size
  const takenAt = new Date().toISOString()

  try {
    // `imageOrientation: 'from-image'` applies the EXIF rotation, which is what
    // keeps iPhone photos from arriving sideways.
    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' })
    const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height))
    const width = Math.round(bitmap.width * scale)
    const height = Math.round(bitmap.height * scale)

    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const context = canvas.getContext('2d')
    if (!context) throw new Error('no 2d context')
    context.drawImage(bitmap, 0, 0, width, height)
    bitmap.close()

    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', QUALITY))
    if (!blob) throw new Error('toBlob failed')

    return { blob, previewUrl: URL.createObjectURL(blob), takenAt, originalBytes }
  } catch {
    // An old browser without createImageBitmap still has to be able to work;
    // the server re-encodes anyway, it just costs more upload.
    return { blob: file, previewUrl: URL.createObjectURL(file), takenAt, originalBytes }
  }
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}
